import { Helmet } from "react-helmet-async";

const SITE_ORIGIN = "https://klexu.com";
const DEFAULT_IMAGE = `${SITE_ORIGIN}/lovable-uploads/19feff6b-2162-4273-bac1-520cb9d3b082.png`;

interface SEOProps {
  title: string;
  description: string;
  /** Route path, e.g. "/about". Defaults to the current location path. */
  path?: string;
  image?: string;
  type?: "website" | "article" | "profile";
  noindex?: boolean;
  children?: React.ReactNode;
}

/**
 * Reusable per-page head metadata: title, description, canonical,
 * Open Graph and Twitter Card tags.
 */
export const SEO = ({
  title,
  description,
  path,
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  children,
}: SEOProps) => {
  const resolvedPath =
    path ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const canonical = `${SITE_ORIGIN}${resolvedPath === "/" ? "/" : resolvedPath.replace(/\/$/, "")}`;
  const absoluteImage = image.startsWith("http") ? image : `${SITE_ORIGIN}${image}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={absoluteImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />
      {children}
    </Helmet>
  );
};

export default SEO;
